import { Building2, Phone, Mail, Globe2 } from "lucide-react";

import { API } from "../lib/api";

function addressLines(profile) {
  return [
    profile?.address,
    profile?.area_locality,
    [profile?.city, profile?.district, profile?.state, profile?.pincode]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);
}

export default function SocietyLetterhead({
  children,
  profile,
  signatory,
  title = "SAMPLE LETTERHEAD",
}) {
  const contacts = [
    profile?.primary_contact && {
      icon: Phone,
      text: profile.primary_contact,
    },
    profile?.official_email && {
      icon: Mail,
      text: profile.official_email,
    },
    profile?.website && {
      icon: Globe2,
      text: profile.website,
    },
  ].filter(Boolean);

  return (
    <article className="bg-white text-slate-900 shadow-sm" data-testid="society-letterhead-preview">
      <header className="border-b-2 border-emerald-700 px-5 py-6 sm:px-8">
        <div className="flex items-start gap-4">
          {profile?.has_logo ? (
            <img
              alt={`${profile.name || "Society"} logo`}
              className="h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20"
              data-testid="society-letterhead-logo"
              src={`${API}/society-setup/profile/logo`}
            />
          ) : (
            <div
              aria-hidden="true"
              className={
                "flex h-16 w-16 shrink-0 items-center justify-center border " +
                "border-emerald-200 bg-emerald-50 text-emerald-700 sm:h-20 sm:w-20"
              }
              data-testid="society-letterhead-text-mark"
            >
              <Building2 className="h-8 w-8" />
            </div>
          )}
          <div className="min-w-0 flex-1 text-center">
            {profile?.regional_name && (
              <p
                className={
                  "font-display text-base font-semibold leading-6 text-emerald-800 " +
                  "sm:text-lg"
                }
              >
                {profile.regional_name}
              </p>
            )}
            <h1
              className={
                "mt-1 font-display text-xl font-bold leading-7 text-slate-950 sm:text-2xl"
              }
            >
              {profile?.name || "Society Name"}
            </h1>
            <div className="mt-2 space-y-0.5 text-xs leading-5 text-slate-600 sm:text-sm">
              {addressLines(profile).map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </div>
        <div
          className={
            "mt-5 grid gap-2 border-t border-slate-200 pt-3 text-xs text-slate-600 " +
            "sm:grid-cols-2"
          }
        >
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {profile?.registration_number && (
              <span>Registration No: {profile.registration_number}</span>
            )}
            {profile?.registration_date && (
              <span>Registration Date: {profile.registration_date}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 sm:justify-end">
            {contacts.map(({ icon: Icon, text }) => (
              <span className="inline-flex items-center gap-1" key={text}>
                <Icon className="h-3 w-3" />
                {text}
              </span>
            ))}
          </div>
        </div>
      </header>
      <div className="min-h-[18rem] px-5 py-8 sm:px-10" data-testid="society-letterhead-body">
        <h2 className="text-center font-display text-lg font-bold tracking-wide text-slate-950">
          {title}
        </h2>
        <div className="mt-8 text-sm leading-7 text-slate-700">{children}</div>
      </div>
      <footer className="border-t border-slate-200 px-5 py-5 text-right text-sm sm:px-10">
        <p className="font-medium">For {profile?.name || "Society"}</p>
        <p className="mt-8">{signatory?.name || "Authorized Signatory"}</p>
        <p className="text-xs text-slate-500">
          {signatory?.designation || "Authorized Signatory"}
        </p>
      </footer>
    </article>
  );
}